import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostBinding,
    Input,
    OnInit,
    Renderer2,
} from '@angular/core';
import { ThemePalette } from '@angular/material/core';

@Component({
    selector: 'vsc-icon',
    template: `<ng-content></ng-content>`,
    styleUrls: ['./icon.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class IconComponent implements OnInit {
    private _icon: string;
    private _style: IconStyle = 'fa-regular';

    @Input() set icon(value: string) {
        const hasIconSet = !!this._icon;
        if (value) {
            this._icon = `fa-${value}`;
        }

        if (hasIconSet) {
            // Process and create icon only if icon is rendered already.
            // Case -> Icon change.
            this.processAndCreateIcon();
        }
    }

    @Input() set iconStyle(value: JustStyle) {
        const hasStyleSet = !!this._style;
        if (value) {
            this._style = `fa-${value}`;
        }

        if (hasStyleSet && this._icon) {
            // Process and create icon only if icon is rendered already
            // Case -> Icon change.
            this.processAndCreateIcon();
        }
    }

    @Input() color: ThemePalette;

    @HostBinding('role') role = 'img';
    @HostBinding('attr.aria-hidden') ariaHidden = 'true';
    @HostBinding('class') get hostClasses(): string {
        const colorClass = this.color ? `mat-${this.color}` : 'default-color';
        return `vsc-icon mat-icon ${colorClass}`;
    }

    constructor(
        private elementRef: ElementRef,
        private renderer: Renderer2,
    ) {}

    ngOnInit(): void {
        const element = this.elementRef.nativeElement as HTMLElement;
        if (!this._icon) {
            this.icon = element.innerHTML?.trim();
        }

        this.processAndCreateIcon();
    }

    private processAndCreateIcon(): void {
        const element = this.elementRef.nativeElement as HTMLElement;
        const content = element.innerHTML;

        const contentSpan = this.renderer.createElement('span');
        contentSpan.innerHTML = content;

        this.renderer.setProperty(element, 'innerHTML', '');
        this.renderer.appendChild(element, contentSpan);

        if (this._icon) {
            const iconElement = this.renderer.createElement('i');

            this.renderer.addClass(iconElement, this._style);
            this.renderer.addClass(iconElement, this._icon);
            this.renderer.appendChild(element, iconElement);
        }
    }
}

type IconStyle = `fa-${JustStyle}`;

type JustStyle = 'regular' | 'solid' | 'light' | 'duotone' | 'thin';
